import React from "react";
import { Plane, User, Info } from "lucide-react";

const ReservationDetails = ({ data }) => {
  if (!data || !data.flight_data || data.flight_data.length === 0) {
    return (
      <div className="p-6 text-center text-red-500">
        ❌ Aucune offre de vol trouvée.
      </div>
    );
  }

  const flights = data.flight_data;
  const passengers = data.passenger_data || [];

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-10">
      {/* Résumé */}
      <div className="shadow-md rounded-2xl bg-white p-6 border border-gray-200">
        <h1 className="text-2xl font-bold mb-2">📑 Détails de la réservation</h1>
        <p className="text-gray-700">
          Référence : <strong>{data.reference || "N/A"}</strong>
        </p>
        <p className="text-gray-700">
          Nombre d’offres disponibles : <strong>{flights.length}</strong>
        </p>
        <p className="text-gray-700">
          Dernière mise à jour : {new Date().toLocaleString()}
        </p>
      </div>

      {/* Offres de vol */}
      {flights.map((flight, idx) => {
        const itineraries = flight.itineraries || [];
        const validatingAirline =
          flight.validatingAirlineCodes?.join(", ") || "N/A";
        const pricingOptions = flight.pricingOptions || {};
      
        const travelerPricings = flight.travelerPricings || [];

        return (
          <div
            key={idx}
            className="space-y-8 border-2 border-gray-200 rounded-2xl shadow-lg p-6 bg-white"
          >
            <h2 className="text-xl font-bold mb-4">
              ✈️ Offre #{idx + 1} – Compagnie : {validatingAirline}
            </h2>

            {/* Infos générales */}
            <section>
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Plane className="w-5 h-5 text-green-600" /> Informations
                Générales
              </h3>
              <ul className="list-disc list-inside text-gray-700">
                <li>Source : {flight.source || "N/A"}</li>
                <li>
                  Dernière date de ticketing : {flight.lastTicketingDate || "N/A"}
                </li>
                <li>Compagnie validante : {validatingAirline}</li>
                <li>
                  Options tarifaires :{" "}
                  {pricingOptions.fareType?.join(", ") || "N/A"}
                </li>
                <li>
                  Bagages inclus uniquement :{" "}
                  {pricingOptions.includedCheckedBagsOnly ? "Oui" : "Non"}
                </li>
              </ul>
            </section>




            {/* Infos passagers */}
            {passengers.length > 0 && (
              <div className="shadow-md rounded-2xl bg-white p-6">
                <h2 className="text-xl font-semibold mb-4">👤 Informations Passagers</h2>
                {passengers.map((p, i) => (
                  <div
                    key={i}
                    className="p-4 mb-3 border rounded-xl bg-gray-50 shadow-sm"
                  >
                    <p className="font-medium">
                      {p.name.firstName} {p.name.lastName} ({p.travelerType})
                    </p>
                    <ul className="list-disc list-inside text-gray-700">
                      <li>Genre : {p.gender}</li>
                      <li>Date de naissance : {p.dateOfBirth}</li>
                      <li>Email : {p.contact?.emailAddress}</li>
                      <li>
                        Téléphone : {p.contact?.phones?.[0]?.countryCallingCode}{" "}
                        {p.contact?.phones?.[0]?.number}
                      </li>
                      {p.documents?.[0] && (
                        <li>
                          Document : {p.documents[0].documentType} –{" "}
                          {p.documents[0].number} (Expire le{" "}
                          {p.documents[0].expiryDate})
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            )}








            {/* Itinéraires */}
            <section>
              <h3 className="text-lg font-semibold mb-2">🛫 Itinéraires</h3>
              {itineraries.length > 0 ? (
                itineraries.map((itin, i) => (
                  <div key={i} className="mb-6">
                    <h4 className="font-medium mb-2">
                      Itinéraire {i + 1} – Durée totale :{" "}
                      {itin.duration || "N/A"}
                    </h4>
                    {itin.segments.map((seg) => {
                      const fd =
                        travelerPricings[0]?.fareDetailsBySegment?.find(
                          (f) => f.segmentId === seg.id
                        );
                      return (
                        <div
                          key={seg.id}
                          className="p-4 mb-3 border rounded-xl bg-gray-50 shadow-sm"
                        >
                          <p className="font-medium">
                            {seg.departure?.iataCode} (T
                            {seg.departure?.terminal || "-"}) –{" "}
                            {seg.arrival?.iataCode} (T{seg.arrival?.terminal || "-"})
                          </p>
                          <p className="text-gray-700">
                            Départ : {seg.departure?.at || "N/A"} | Arrivée :{" "}
                            {seg.arrival?.at || "N/A"}
                          </p>
                          <p className="text-gray-700">
                            Compagnie :{" "}
                            <strong>
                              {seg.carrierCode}
                              {seg.number}
                            </strong>{" "}
                            | Avion : {seg.aircraft?.code || "N/A"}
                          </p>
                          <p className="text-gray-600">Durée : {seg.duration}</p>
                          <p className="text-gray-600">
                            Arrêts : {seg.numberOfStops}
                          </p>
                          {fd && (
                            <>
                              <p className="text-gray-600">
                                Classe : {fd.class || "N/A"} | Tarif :{" "}
                                {fd.fareBasis || "N/A"}
                              </p>
                              <p className="text-gray-600">
                                Offre : {fd.brandedFareLabel || fd.brandedFare}
                              </p>
                              <p className="text-gray-600">
                                🧳 Bagages inclus :{" "}
                                {fd?.includedCheckedBags?.quantity || "N/A"}{" "}
                                valise(s)
                              </p>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">Aucun itinéraire disponible.</p>
              )}
            </section>



            {/* Tarifs par voyageur */}
            {travelerPricings.length > 0 && (
              <section>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" /> Tarifs par Voyageur
                </h3>
                <div className="grid gap-4">
                  {travelerPricings.map((tp, i) => (
                    <div
                      key={i}
                      className="p-4 border rounded-xl bg-gray-50 shadow-sm"
                    >
                      <p className="font-medium">
                        Voyageur {tp.travelerId} ({tp.travelerType})
                      </p>

                      {tp.fareDetailsBySegment?.map((fd, j) => (
                        <div key={j} className="mt-2 ml-3 text-gray-600">
                          <p>
                            Segment {fd.segmentId} | Classe : {fd.class} | Tarif :{" "}
                            {fd.fareBasis}
                          </p>
                          <p>
                            🧳 Bagages :{" "}
                            {fd?.includedCheckedBags?.quantity || "N/A"} valise(s)
                          </p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        );
      })}



      {/* Informations générales */}
      <div className="shadow-md rounded-2xl bg-white p-6">
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <Info className="w-5 h-5 text-red-600" /> Informations complémentaires
        </h2>
        <p className="text-gray-700">
          ✅ Toutes ces informations proviennent directement des offres de vol
          Amadeus.
          ⚠️ Les horaires et prix peuvent varier en fonction de la disponibilité au
          moment de la réservation définitive.
        </p>
      </div>
    </div>
  );
};

export default ReservationDetails;
